import React, { useMemo } from 'react';
import { Box, Heading, Text, useBreakpointValue } from '@chakra-ui/react';
import { useAuth } from '@/context/AuthContext';
import { formatAddress } from '@/utils/addressUtils.ts';
import { useCurrentHouse } from '@/context/CurrentHouseContext.tsx';
import { useCrossfadeValue } from '@/hooks/ui/useCrossfadeValue.ts';
import { EASE_OUT, HEADING_MOUNT_ENTER_CSS } from '@/components/ui/motion.ts';

export type AssistantHeadingMode = 'ask' | 'record' | 'task' | 'contact';

// The lead-in clause that rotates with the composer mode; the address completes the line.
const HEADING_LEAD: Record<AssistantHeadingMode, string> = {
	ask: 'Any questions about',
	record: "What's new about",
	task: 'What needs doing at',
	contact: 'Who helps at',
};

// ── Typography — every line and segment is tunable independently from here ──────────────────────
// Vertical gap (Chakra spacing units) between the greeting line and the prompt line.
const HEADING_LINE_GAP = 1;
const HEADING_STYLE = {
	// "Hi {name}," line
	greeting: { fontSize: { base: 'lg', md: '2xl' }, fontWeight: 'medium', color: 'brand.fg' },
	// the first name inside the greeting
	name: { fontWeight: 'medium', color: 'brand.fg' },
	// the rotating prompt line (the lead-in words + "?")
	prompt: { fontSize: { base: 'lg', md: '2xl' }, fontWeight: 'medium', color: 'brand.fg' },
	// the address inside the prompt
	address: { fontWeight: 'medium', color: 'brand.fg' },
};

// Crossfade timing for the rotating line. Exit is a touch faster than entry (per the animation skill).
const PROMPT_OUT_MS = 120;
const PROMPT_IN_MS = 160;

// Above this length the address wraps to its own line on mobile so the prompt doesn't crowd.
const REALLY_LONG_ADDRESS = 22;

interface AssistantHeadingProps {
	/** Drives the rotating prompt: the assistant's ask line, or the add composer's record/task line. */
	mode?: AssistantHeadingMode;
}

/**
 * Personalised greeting above the assistant composer. The second line rotates with the composer
 * mode (ask -> record -> task -> contact) and crossfades on change; the "Hi {name}," line stays put since it
 * isn't changing. Typography lives in HEADING_STYLE above so each piece can be tuned in one place.
 */
const AssistantHeading: React.FC<AssistantHeadingProps> = (props) => {
	const { mode = 'ask' } = props;
	const { user } = useAuth();
	const firstName = user?.first_name ?? null;
	const { house } = useCurrentHouse();

	const formattedAddress = useMemo(() => (house ? formatAddress(house.address).firstPart : ''), [house]);
	const isMobile = useBreakpointValue({ base: true, md: false });

	const { displayed, hidden } = useCrossfadeValue(mode, PROMPT_OUT_MS);

	return (
		<Heading
			display="flex"
			flexDirection="column"
			alignItems="center"
			textAlign="center"
			gap={HEADING_LINE_GAP}
			css={HEADING_MOUNT_ENTER_CSS}
		>
			<Box as="span" display="block" {...HEADING_STYLE.greeting}>
				Hi
				{firstName && (
					<Text as="span" {...HEADING_STYLE.name}>
						{' '}
						{firstName}
					</Text>
				)}
				,
			</Box>

			<Box
				as="span"
				display="block"
				data-hidden={hidden ? '' : undefined}
				{...HEADING_STYLE.prompt}
				css={{
					transition: `opacity ${PROMPT_IN_MS}ms ${EASE_OUT}, filter ${PROMPT_IN_MS}ms ${EASE_OUT}, transform ${PROMPT_IN_MS}ms ${EASE_OUT}`,
					'&[data-hidden]': {
						opacity: 0,
						// Blur masks the crossfade so the two phrases read as one morph, not two strings.
						filter: 'blur(2px)',
						transform: 'translateY(-4px)',
						transitionDuration: `${PROMPT_OUT_MS}ms`,
					},
					'@media (prefers-reduced-motion: reduce)': {
						transition: `opacity ${PROMPT_IN_MS}ms ease`,
						'&[data-hidden]': { opacity: 0, filter: 'none', transform: 'none' },
					},
				}}
			>
				{HEADING_LEAD[displayed]} {isMobile && formattedAddress.length > REALLY_LONG_ADDRESS && <br />}
				<Text as="span" {...HEADING_STYLE.address}>
					{formattedAddress}
				</Text>
				?
			</Box>
		</Heading>
	);
};

export default AssistantHeading;
